import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Badge } from './Badge';

export interface MealMacros {
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
}

export interface MealCardProps {
  name: string;
  timeLabel: string;
  macros: MealMacros;
  note?: string | null;
  photoUrl?: string | null;
  /** Komentar trenera uz obrok (`meals.trainer_comment`); null kad ga nema. */
  trainerComment?: string | null;
  trainerCommentLabel?: string;
  labels: { kcal: string; protein: string; carbs: string; fat: string };
  /** Radnja uz obrok (npr. brisanje). Stoji uz vrijeme, izvan opisa. */
  action?: ReactNode;
  /**
   * Otvara fotografiju u punoj veličini. Sličica je 80 px — dovoljno da se
   * prepozna da obrok IMA sliku, premalo da se vidi što je na tanjuru, a upravo
   * to je jedini razlog zašto je klijent slika.
   */
  onPhotoOpen?: () => void;
  photoOpenLabel?: string;
  /**
   * Dodatak na dnu kartice — trenerov komentar u pisanju stoji ovdje, unutar
   * kartice obroka. Izvan nje bi gumb visio na pozadini i izgledao kao da
   * pripada sljedećem obroku.
   */
  footer?: ReactNode;
  className?: string;
}

export function MealCard({
  name,
  timeLabel,
  macros,
  note,
  photoUrl,
  trainerComment,
  trainerCommentLabel,
  labels,
  action,
  onPhotoOpen,
  photoOpenLabel,
  footer,
  className,
}: MealCardProps) {
  return (
    <article
      className={cx(
        'flex gap-4 rounded-xl border border-line-subtle bg-surface p-4 shadow-card',
        className,
      )}
    >
      {photoUrl &&
        (onPhotoOpen ? (
          <button
            type="button"
            onClick={onPhotoOpen}
            aria-label={photoOpenLabel}
            className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-md"
          >
            <img
              src={photoUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
            {/* Tiha naznaka da se slika može otvoriti; bez nje je gumb nevidljiv. */}
            <span
              aria-hidden="true"
              className="absolute inset-0 bg-inverse/0 transition-colors group-hover:bg-inverse/20"
            />
          </button>
        ) : (
          <img
            src={photoUrl}
            alt=""
            loading="lazy"
            className="h-20 w-20 shrink-0 rounded-md object-cover"
          />
        ))}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="truncate font-display text-[18px] font-semibold text-ink">{name}</h3>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-[12px] text-ink-muted">{timeLabel}</span>
            {action}
          </div>
        </div>
        {/* Opis obroka je uvijek vidljiv — bug `.thumb-cap` iz ARHITEKTURA.md sekcije 1. */}
        {note && <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{note}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge tone="accent">
            {macros.kcal} {labels.kcal}
          </Badge>
          <Badge>
            {labels.protein} {macros.proteinG} g
          </Badge>
          <Badge>
            {labels.carbs} {macros.carbsG} g
          </Badge>
          <Badge>
            {labels.fat} {macros.fatG} g
          </Badge>
        </div>
        {trainerComment && (
          <p className="mt-3 rounded-md bg-bg-alt px-3 py-2 text-[13px] leading-relaxed text-ink-muted">
            {trainerCommentLabel && <span className="eyebrow mr-2">{trainerCommentLabel}</span>}
            {trainerComment}
          </p>
        )}
        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </article>
  );
}
